// src/components/common/ProgressBar.jsx
import { useTranslation } from "react-i18next";

const stages = ["start", "text", "media", "publish"];

/**
 * Where the user is in the post-creation task. Always four stages.
 *   stage   — "start" | "text" | "media" | "publish"
 *   variant — "header" (compact tabs-style track, lives in the Navbar)
 *             "mobile" (step count + segments, lives at the top of the page)
 */
const ProgressBar = ({ stage, variant = "header" }) => {
  const { t } = useTranslation();

  const currentIndex = Math.max(0, stages.indexOf(stage));
  const total = stages.length;

  if (variant === "mobile") {
    return (
      <nav
        aria-label={t("progress.label")}
        className="bg-black-0 px-4 pt-3 pb-3 border-b border-black-30"
      >
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-sm font-bold text-black-800">
            {t(`progress.stages.${stages[currentIndex]}`)}
          </span>
          <span className="text-sm text-black-600">
            {t("progress.stepOf", { current: currentIndex + 1, total })}
          </span>
        </div>
        <div className="flex gap-1" role="presentation">
          {stages.map((s, i) => (
            <span
              key={s}
              className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                i <= currentIndex ? "bg-primary-400" : "bg-black-40"
              }`}
            />
          ))}
        </div>
      </nav>
    );
  }

  return (
    <nav aria-label={t("progress.label")} className="w-full">
      <ol className="flex gap-2 w-full items-center">
        {stages.map((s, i) => {
          const isDone = i < currentIndex;
          const isCurrent = i === currentIndex;
          return (
             <li
              key={s}
              aria-current={isCurrent ? "step" : undefined}
              className={`flex flex-1 min-w-0 items-center justify-center gap-2 py-1.5 px-3 rounded-full text-base border transition-all duration-300 ${
                isCurrent
                  ? "bg-primary-500 border-primary-400 text-black-900 font-bold"
                  : isDone
                    ? "bg-primary-100 !border-primary-100 text-black-800"
                    : "bg-black-20 border-black-40 text-black-600"
              }`}
            >
              <span
                className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-bold flex-shrink-0 ${
                  isCurrent
                    ? "bg-black-900 text-black-0"
                    : isDone
                      ? "bg-primary-500 text-black-900"
                      : "bg-black-40 text-black-600"
                }`}
              >
                {isDone ? "✓" : i + 1}
              </span>
              <span className={`truncate ${isCurrent ? "" : "hidden lg:inline"}`}>
                {t(`progress.stages.${s}`)}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default ProgressBar;